import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Plus, X, MapPin, Calendar } from 'lucide-react';
import { EventTypePicker } from '@/components/EventTypePicker';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const STEPS = ['Type', 'Details', 'Invite', 'Review'];

export default function EventWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({
    type: 'road_trip',
    title: '',
    description: '',
    start_date: today(0),
    end_date: today(2),
    location_text: '',
    origin: '',
    destination: '',
    cover_image: '',
    visibility: 'private',
  });
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('attendee');

  const next = () => {
    if (step === 1) {
      if (!data.title.trim()) return toast.error('Please add a title');
      if (new Date(data.end_date) < new Date(data.start_date)) return toast.error('End date must be after start date');
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const addInvite = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return toast.error('Enter a valid email');
    if (invites.some((i) => i.email === email)) return toast.error('Already added');
    setInvites((arr) => [...arr, { email, role: inviteRole }]);
    setInviteEmail('');
  };
  const removeInvite = (email) => setInvites((arr) => arr.filter((i) => i.email !== email));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/events', data);
      const ev = r.data;
      // send invites
      for (const inv of invites) {
        try {
          await api.post(`/events/${ev.id}/invite`, inv);
        } catch (err) {
          console.error(`Failed to invite ${inv.email}:`, err);
        }
      }
      toast.success('Event created');
      navigate(`/events/${ev.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="event-wizard">
      <div className="flex items-center gap-3 mb-3">
        <button onClick={() => navigate(-1)} data-testid="wizard-close" className="h-9 w-9 rounded-xl border bg-card flex items-center justify-center">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div>
          <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</div>
          <h1 className="font-serif text-2xl font-semibold leading-tight">{STEPS[step]}</h1>
        </div>
      </div>

      <div className="h-1 w-full rounded-full bg-secondary mb-5 overflow-hidden">
        <motion.div className="h-full gradient-fab" initial={{ width: 0 }} animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.3 }} />
      </div>

      {step === 0 && (
        <div data-testid="wizard-step-type">
          <p className="text-sm text-muted-foreground mb-4">What kind of memory are you making?</p>
          <EventTypePicker value={data.type} onChange={(v) => setData((d) => ({ ...d, type: v }))} />
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="wizard-step-details">
          <div>
            <Label>Title</Label>
            <Input value={data.title} onChange={(e) => setData((d) => ({ ...d, title: e.target.value }))} placeholder="e.g., Pacific Coast Road Trip" data-testid="event-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={data.description} onChange={(e) => setData((d) => ({ ...d, description: e.target.value }))} placeholder="Why this trip is special…" data-testid="event-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Start</Label>
              <Input type="date" value={data.start_date} onChange={(e) => setData((d) => ({ ...d, start_date: e.target.value }))} data-testid="event-start-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> End</Label>
              <Input type="date" value={data.end_date} onChange={(e) => setData((d) => ({ ...d, end_date: e.target.value }))} data-testid="event-end-input" />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Location</Label>
            <Input value={data.location_text} onChange={(e) => setData((d) => ({ ...d, location_text: e.target.value }))} placeholder="City, region, or venue" data-testid="event-location-input" />
          </div>
          {data.type === 'road_trip' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Origin</Label>
                <Input value={data.origin} onChange={(e) => setData((d) => ({ ...d, origin: e.target.value }))} placeholder="From" data-testid="event-origin-input" />
              </div>
              <div>
                <Label>Destination</Label>
                <Input value={data.destination} onChange={(e) => setData((d) => ({ ...d, destination: e.target.value }))} placeholder="To" data-testid="event-destination-input" />
              </div>
            </div>
          )}
          <div>
            <Label>Cover image URL (optional)</Label>
            <Input value={data.cover_image} onChange={(e) => setData((d) => ({ ...d, cover_image: e.target.value }))} placeholder="https://…" data-testid="event-cover-input" />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="event-visibility-private" onClick={() => setData((d) => ({ ...d, visibility: 'private' }))} className={`flex-1 px-3 py-2 rounded-xl border text-sm ${data.visibility === 'private' ? 'border-primary bg-primary/10' : 'border-border'}`}>Private</button>
            <button type="button" data-testid="event-visibility-public" onClick={() => setData((d) => ({ ...d, visibility: 'public' }))} className={`flex-1 px-3 py-2 rounded-xl border text-sm ${data.visibility === 'public' ? 'border-primary bg-primary/10' : 'border-border'}`}>Public</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="wizard-step-invite">
          <p className="text-sm text-muted-foreground">Add the people who should be on this memory.</p>
          <div className="flex gap-2">
            <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="name@domain.com" data-testid="invite-email-input" className="flex-1" />
            <select data-testid="invite-role-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="px-3 rounded-xl border border-input bg-card text-sm">
              <option value="attendee">Attendee</option>
              <option value="organizer">Organizer</option>
              <option value="speaker">Speaker</option>
              <option value="vendor">Vendor</option>
              <option value="trip_member">Trip member</option>
            </select>
            <Button type="button" onClick={addInvite} data-testid="invite-add-button" className="rounded-xl">Add</Button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="invite-list">
            {invites.map((i) => (
              <Badge key={i.email} variant="outline" className="rounded-full pl-2 pr-1 py-1 gap-1 text-xs" data-testid={`invite-chip-${i.email}`}>
                <span className="truncate max-w-[180px]">{i.email}</span>
                <span className="px-1.5 text-[10px] rounded-full bg-secondary text-secondary-foreground">{i.role}</span>
                <button onClick={() => removeInvite(i.email)} className="ml-1 h-5 w-5 rounded-full hover:bg-secondary flex items-center justify-center" aria-label={`Remove ${i.email}`}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
            {invites.length === 0 && <p className="text-xs text-muted-foreground">You can also add people later.</p>}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="wizard-step-review">
          <Card className="p-4 rounded-2xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{labelType(data.type)}</span>
              <Badge variant="outline" className="rounded-full text-[10px] uppercase">{data.visibility}</Badge>
            </div>
            <h3 className="font-serif text-2xl font-semibold mb-1">{data.title || 'Untitled'}</h3>
            <p className="text-sm text-muted-foreground">{data.description || 'No description yet.'}</p>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div><div className="text-muted-foreground">Start</div><div className="font-medium">{data.start_date}</div></div>
              <div><div className="text-muted-foreground">End</div><div className="font-medium">{data.end_date}</div></div>
              {data.location_text && <div className="col-span-2"><div className="text-muted-foreground">Where</div><div className="font-medium">{data.location_text}</div></div>}
              {data.origin && <div><div className="text-muted-foreground">From</div><div className="font-medium">{data.origin}</div></div>}
              {data.destination && <div><div className="text-muted-foreground">To</div><div className="font-medium">{data.destination}</div></div>}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Invitations</div>
            <div className="flex flex-wrap gap-2">
              {invites.length === 0 ? <p className="text-sm text-muted-foreground">None yet</p> : invites.map((i) => (
                <Badge key={i.email} variant="outline" className="rounded-full text-xs">{i.email} · {i.role}</Badge>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div className="sticky bottom-24 mt-8 flex items-center gap-2 bg-background/90 backdrop-blur py-3">
        <Button variant="outline" onClick={back} disabled={step === 0 || busy} data-testid="wizard-back-button" className="rounded-xl flex-1"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} data-testid="wizard-next-button" className="rounded-xl flex-1 gradient-fab text-white">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
        ) : (
          <Button onClick={submit} disabled={busy} data-testid="wizard-create-button" className="rounded-xl flex-1 gradient-fab text-white"><Plus className="h-4 w-4 mr-1" /> {busy ? 'Creating…' : 'Create'}</Button>
        )}
      </div>
    </div>
  );
}

function labelType(t) {
  const map = { road_trip: 'Road Trip', vacation: 'Vacation', conference: 'Conference', social: 'Social', custom: 'Custom' };
  return map[t] || 'Event';
}

function today(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}
